import type { SchematizedValues } from "components/Form/components/SchemaForm/SchemaForm.types";
import { IconCollection } from "components/Icon/IconCollection";
import { DASHBOARD_DOCS_URL_PATH } from "containers/Dashboard/DashboardDocsLink";
import { ResourceConfig } from "config/config";
import { Action, ActionSignature } from "containers/Resources/components/CommonActions/Action";
import {
    deleteActionSignature,
    DeleteListAction,
    deletePermanentlyActionSignature,
    downloadActionSignature,
    duplicateActionSignature,
    exportActionSignature,
    isDeleted,
    moveToLocationActionSignature,
    PermanentlyDeleteListAction,
} from "containers/Resources/components/CommonActions/CommonActions";
import {
    commentsTab,
    logsTab,
    logsTabKey,
    metadataTab,
    referencesUsedByTab,
    valueAndSchema,
} from "containers/Resources/screens/ResourcesSingle/constants";
import { YAMLException } from "js-yaml";
import { isEqual } from "lodash";
import { getDashboardViewLink } from "utils/service";
import { parseYaml } from "utils/valueTransformers";
import { dashboardWidgetSchema } from "./dashboardWidgetSchema";
import restClient from "api/restClient";
import { seedRecordFormWidgetFields } from "containers/Dashboard/widgets/recordFormEditorSeed";

import resource from "./resource";

const fetchObjectTemplateSchema = async (objectTemplateId: string): Promise<unknown> => {
    const response: any = await restClient.get(`object_template/${objectTemplateId}`, {
        params: { fields: "schema" },
    });
    return response?.data?.object_template?.schema ?? null;
};

const deriveDashboardEditorValues = (previous: any, next: any) =>
    seedRecordFormWidgetFields(previous, next, fetchObjectTemplateSchema);

const resourceName = "dashboard";

const ViewDashboardAction: Action = (data, _dispatch, history) => {
    history.push(getDashboardViewLink(data.resourceData.name));
    return Promise.resolve(null);
};

const viewDashboardActionSignature: ActionSignature = {
    key: "viewDashboard",
    action: ViewDashboardAction,
    label: () => "View",
    icon: () => IconCollection.monitorDashboard,
    disableWhen: isDeleted,
};

const config: ResourceConfig = {
    label: "Dashboard",
    icon: IconCollection.monitorDashboard,
    resourceName,
    resourceClass: "resource",
    defaultActiveTab: logsTabKey,
    layout: [
        {
            partial: "pageHeaderForm",
            config: {
                editIcon: true,
                curatedIcons: true,
                docsUrlPath: DASHBOARD_DOCS_URL_PATH,
            },
        },
        { partial: "form" },
        {
            partial: "tabs",
            config: {
                tabs: [logsTab.tab, commentsTab.tab, referencesUsedByTab.tab, metadataTab.tab],
                content: [logsTab.content, commentsTab.content, referencesUsedByTab.content, metadataTab.content],
            },
        },
    ],
    graphql: {
        resource: resourceName,
        resourceBody: `
            ${resource.graphql.resourceBody}
            icon
            value
        `,
        resourceBodyListing: `
            ${resource.graphql.resourceBody}
            icon
        `,
        createVariables: `
            ${resource.graphql.createVariables(resourceName)}
            $icon: String,
            $value: SchemalessData,
        `,
        createQuery: `
            ${resource.graphql.createQuery(resourceName)}
            icon: $icon,
            value: $value,
        `,
        updateVariables: `
            ${resource.graphql.updateVariables(resourceName)}
            $icon: String,
            $value: SchemalessData,
        `,
        updateQuery: `
            ${resource.graphql.updateQuery(resourceName)}
            icon: $icon,
            value: $value,
        `,
    },
    actions: {
        shown: [viewDashboardActionSignature],
        hidden: [
            duplicateActionSignature,
            downloadActionSignature,
            exportActionSignature,
            moveToLocationActionSignature,
            deleteActionSignature,
            deletePermanentlyActionSignature,
        ],
    },
    listActions: {
        shown: [],
        hidden: [
            { key: "delete", action: DeleteListAction },
            { key: "deletePermanently", action: PermanentlyDeleteListAction },
        ],
    },
    form: [
        {
            // No-code widget editor: render dashboard.value as a structured
            // SchemaForm driven by the static dashboardWidgetSchema (a
            // discriminated union over the registered widget types). The
            // SchemaForm component's built-in "Code view" toggle preserves a
            // raw-YAML escape hatch for advanced editing and for
            // forward-compatibility with widget types a newer FE may add
            // (operator-task-dashboard-widget-editor-form).
            name: valueAndSchema,
            component: "schemaForm",
            fieldProps: {
                lang: "yaml",
                isReadOnly: false,
                canEnableSaveManually: true,
                // Picking a record type / object template in a record_form
                // widget with no Fields fills them with the create-screen fields.
                deriveValues: deriveDashboardEditorValues,
            },
            // The widget schema is a static module constant, not a persisted
            // per-record field — only the edited values map back to `value`.
            remappingSubmitDto: (value: SchematizedValues) => [
                {
                    key: "value",
                    value: value.values,
                },
            ],
            isEqual: (valueA, valueB) => isEqual(valueA, valueB),
            validation: (value: SchematizedValues) => {
                try {
                    parseYaml(value.values);
                    return undefined;
                } catch (error: any) {
                    const typedError: YAMLException = error;
                    return `${typedError?.name}: ${typedError?.reason}`;
                }
            },
        },
    ],
    formDTO: {
        initialValueDTO: (data: any) => {
            const valueAndSchemaFieldValues: SchematizedValues = {
                values: data?.value,
                schema: dashboardWidgetSchema,
            };
            return { ...data, [valueAndSchema]: valueAndSchemaFieldValues };
        },
    },
};
export default config;
